// Canonical service-type → human label map + humanizer.
//
// Why this lives in /shared: the same slug ("home_policy") must render
// identically wherever it appears — the household page, the service-record
// list/detail, the Cleanup drawer, AND the Worker when it materializes a
// service record from a won opportunity. Before this module, the map was
// duplicated in a few web files and the backend had none, so a service
// record created without a display_label leaked the raw slug ("home_policy")
// in the Cleanup drawer. One source of truth prevents that drift.
//
// This is a DISPLAY helper only — the stored source of truth stays the
// `service_records.service_type` slug + optional `display_label`.

export const SERVICE_TYPE_LABEL: Record<string, string> = {
  // Insurance
  home_policy: "Home Policy",
  auto_policy: "Auto Policy",
  umbrella_policy: "Umbrella Policy",
  life_policy: "Life Policy",
  health_policy: "Health Policy",
  medicare_policy: "Medicare Policy",
  disability_policy: "Disability Policy",
  // Insurance types added for the household-card title fix (Mylah,
  // 2026-08-03) — every value in insurance-policies.ts' VALID_POLICY_TYPES
  // now has a label here, so a policy card leads with the type ("Auto
  // Policy") instead of falling through to a raw slug or the stored
  // carrier-prefixed display_label.
  renters_policy: "Renters Policy",
  condo_policy: "Condo Policy",
  dwelling_fire_policy: "Dwelling / Landlord Policy",
  secondary_home_policy: "Secondary / Vacation Home Policy",
  watercraft_policy: "Watercraft / Boat Policy",
  motorcycle_policy: "ATV / Motorcycle Policy",
  rv_policy: "RV / Travel Trailer Policy",
  golf_cart_policy: "Golf Cart Policy",
  flood_policy: "Flood Policy",
  commercial_policy: "Commercial Policy",
  long_term_care_policy: "Long Term Care Policy",
  other_policy: "Other Policy",
  // Financial account registrations (2026-09-10). Spelled the way an
  // advisor says them out loud, not the way the slug is stored —
  // humanizeServiceType's fallback would render these "Ira Roth",
  // "401k" and "Sep Ira".
  ira_traditional: "Traditional IRA",
  ira_roth: "Roth IRA",
  sep_ira: "SEP IRA",
  simple_ira: "SIMPLE IRA",
  "401k": "401(k)",
  joint: "Joint Account",
  trust: "Trust Account",
  // Mortgage
  refinance_mortgage: "Refinance Mortgage",
  purchase_mortgage: "Purchase Mortgage",
  private_money_loan: "Private Money Loan",
  // The OS-created Processing spelling of the same product (see
  // shared/private-money/record-classification.ts). Named, not Title-Cased,
  // so it never reads "Private Money Loan Progression".
  private_money_loan_progression: "Private Money Loan",
  reverse_mortgage: "Reverse Mortgage",
  heloc: "HELOC",
  heloan: "HELOAN",
  // The purpose-unknown generic. Reads as a plain statement of what is
  // known rather than a guess — 359 imported files where the source
  // never said purchase or refinance.
  mortgage_loan: "Mortgage Loan",
  // Financial
  managed_portfolio: "Managed Portfolio",
  financial_plan: "Financial Plan",
  retirement_plan: "Retirement Plan",
  advisory_relationship: "Advisory Relationship",
  // Planning
  medicare_case: "Medicare Case",
  estate_plan: "Estate Plan",
  life_case: "Life Case",
  health_case: "Health Case",
  disability_case: "Disability Case",
  long_term_care_case: "Long-Term Care Case",
  dental_case: "Dental Case",
  vision_case: "Vision Case",
  // Explicit entry: the fallback humanizer would drop the hyphen
  // ("Short Term Medical Case").
  short_term_medical_case: "Short-Term Medical Case",
  // Realty
  listing: "Listing",
  home_purchase: "Home Purchase",
  // Rentals (2026-09-22): we represent the landlord / the tenant.
  rental_listing: "Rental Listing",
  home_rental: "Home Rental",
  sale_transaction: "Sale Transaction",
};

/**
 * Transaction (Processing) types whose Title-Cased slug is not what the
 * team calls the thing.
 *
 * These are a SEPARATE namespace from service types — a Processing record's
 * `transaction_type` names the board it runs on, and the two vocabularies
 * only partly overlap. Kept here rather than in a seventh local helper
 * because that is exactly how this broke: `life_health_case` has had a
 * proper name (BOARD_LABELS in shared/db/company-stage-templates.ts) since
 * the board was renamed, but six different files each Title-Cased the raw
 * slug themselves, so Ryan read "Life Health Case" on the household page,
 * the company page, the processing board and the transaction page while
 * the board itself said "Health & Medicare Case".
 *
 * BOARD_LABELS stays the authority on what a BOARD is called; this map is
 * what a RECORD on it is called, and the two must agree. A test in
 * tests/unit/company-stage-templates.test.ts asserts they do, because two
 * hand-maintained maps of the same words drift the moment nobody is
 * looking.
 */
export const TRANSACTION_TYPE_LABEL: Record<string, string> = {
  // Planning. Renamed 2026-08-29 when life split onto its own board: the
  // combined name read as "a life insurance case" when 29 of 33 records
  // were Medicare/health. The slug is deliberately unchanged.
  life_health_case: "Health & Medicare Case",
  planning_life_case: "Life Case",
  estate_plan_case: "Estate Plan Case",
  // Realty's rental board (2026-09-22). Title-Casing gives "Realty
  // Rental", which is right, but it is listed so the board label and the
  // record label are asserted to agree.
  realty_rental: "Realty Rental",
  // Deliberately only the types whose Title-Cased slug CONTRADICTS the
  // board — not every type whose label could be prettier. Renaming what
  // the team already reads correctly ("Mortgage Loan Progression",
  // "Financial Client Onboarding") is a vocabulary decision for the people
  // who own those boards, not a side effect of fixing this one.
};

/**
 * A human-readable label for a transaction_type slug. Consults the
 * transaction map first, then the service-type map (several transaction
 * types reuse a service-type slug verbatim — `estate_plan`,
 * `private_money_loan`, `realty_purchase`), then Title-Cases.
 *
 * Use this anywhere a Processing record's type is shown to a person. A
 * local Title-Case helper is how the label map loses its reach.
 */
export function humanizeTransactionType(slug: string): string {
  if (!slug) return "";
  return TRANSACTION_TYPE_LABEL[slug] ?? humanizeServiceType(slug);
}

// Words that are acronyms when spoken, so Title Case must not render them
// "Atv" and "Rv" (Brandon's quote types, 2026-09-28). Deliberately short:
// every entry changes how that word reads on every screen using
// titleCaseSlug, so add one only for a slug that actually carries it.
// "ltc" (employer groups, the Nov 3 audit's D3): "group_ltc" read "Group
// Ltc" on the sale's title and breadcrumb.
const SLUG_ACRONYMS = new Set(["atv", "rv", "ltc"]);

// Whole slugs whose right words are not their Title Case. Brandon,
// 2026-09-29: carriers write ATVs and motorcycles on one policy, and a
// travel trailer rides on an RV (or auto) policy, so the quote cards say
// both words. The two retired slugs (offered for one night on staging
// only) read the same as the type they fold into.
const SLUG_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  motorcycle_policy_quote: "ATV / Motorcycle Policy Quote",
  atv_policy_quote: "ATV / Motorcycle Policy Quote",
  rv_policy_quote: "RV / Travel Trailer Policy Quote",
  travel_trailer_policy_quote: "RV / Travel Trailer Policy Quote",
  landlord_policy_quote: "Landlord / Rental Policy Quote",
};

/**
 * Title Case for an underscored slug, keeping known acronyms upper case:
 * "rv_park_visit" -> "RV Park Visit". Same output as the old
 * `\b\w` upper-caser for every other slug.
 */
export function titleCaseSlug(slug: string): string {
  const override = SLUG_LABEL_OVERRIDES[slug];
  if (override) return override;
  return slug
    .split("_")
    .map((w) =>
      SLUG_ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w ? w[0]!.toUpperCase() + w.slice(1) : w,
    )
    .join(" ");
}

/**
 * A human-readable label for a service_type slug. Falls back to a
 * generic Title-Case of the underscored slug for any type not in the
 * map, so an unknown/new type never renders as a bare "some_new_thing".
 */
export function humanizeServiceType(slug: string): string {
  if (!slug) return "";
  return (
    SERVICE_TYPE_LABEL[slug] ??
    slug
      .split("_")
      .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
      .join(" ")
  );
}

// Short, board/column-width labels for planning_cases.plan_tier — same 5
// Medicare values PlanningCaseProductDetails.tsx's MEDICARE_TIER_OPTIONS
// offers in the case editor, condensed for read-only display (S23,
// andrea-planning-ops.md §7b: Andrea can't tell PDP from MAPD
// anywhere on the renewal board). ACA's marketplace_* metal-level
// values have no short-form need yet — not covered here.
const MEDICARE_TIER_LABEL: Record<string, string> = {
  ma: "MA/MAPD",
  pdp: "PDP",
  medigap_g: "Medigap G",
  medigap_n: "Medigap N",
  medigap_f: "Medigap F",
};

/** Short display label for a Medicare plan_tier value; null for
 *  anything not in the map (ACA metal levels, empty). */
export function medicareTierLabel(tier: string | null): string | null {
  if (!tier) return null;
  return MEDICARE_TIER_LABEL[tier] ?? null;
}
